import { Component } from '@angular/core';
import { AuthService, BreadcrumbService, ConfirmDialogService, ProductNameService, StateService } from 'src/app/services';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfigService } from 'src/app/services/config.service';
import { ActivatedRoute, Router } from '@angular/router';
import { 
  ListRenderedOutputsRequest, ListRenderedOutputsResponse, ProductName, RenderedOutputStatus, 
  RenderedOutputSummary, SituViewWebApiClient,SetRenderedOutputStatusRequest, SetRenderedOutputStatusResponse
} from 'situview-webapi-client';
import { MatTableDataSource } from '@angular/material/table';
import { PageEvent } from '@angular/material/paginator';
import { ViewControls } from 'src/app/shared/models/view-controls';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { ConfirmDialogData } from 'src/app/components/confirm-dialog/ConfirmDialogData';

@Component({
  selector: 'situ-browse',
  templateUrl: './situ-browse.component.html',
  styleUrls: ['./situ-browse.component.scss']
})
export class SituBrowseComponent extends SituBaseComponent {
  private readonly DefaultPageSize: number = 12;

  /* view data */
  viewControls = new ViewControls(this.DefaultPageSize, 0, 0, [this.DefaultPageSize, 25, 50, 100]);
  dataSource = new MatTableDataSource<RenderedOutputSummary>();

  /* search params */
  private readonly ProductParamName: string = "product";
  readonly FilterParamName: string = "filter";
  readonly StyleParamName: string = "style";
  readonly ConfigParamName: string = "config";
  readonly HomeParamName: string = "home";
  private readonly LiveOnlyParamName: string = "liveOnly";
  private readonly PageSizeParamName: string = "pageSize";
  isProductionOnly: boolean = false;
  filter: string = "";
  product: ProductName | undefined;
  style: string | undefined
  config: string | undefined
  home: string | undefined

  productNames = ProductName;

  /* loading */
  private _loading = false;
  get loading(): boolean { return this._loading; }
  set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private snackBar: MatSnackBar,
    private breadcrumbService: BreadcrumbService,
    private configService: ConfigService,
    private route: ActivatedRoute,
    private router: Router,
    private situViewClient: SituViewWebApiClient,
    private productNameService: ProductNameService,
    private dialogService: ConfirmDialogService) { 
      super(authService, stateService);
      if (this.route.snapshot.params[this.ProductParamName]) {
        let numberValue = parseInt(this.route.snapshot.params[this.ProductParamName]);
        if (!isNaN(numberValue)) {
          this.product = numberValue;
        }
      }
      if (this.route.snapshot.queryParams[this.FilterParamName]) {
        this.filter = this.route.snapshot.queryParams[this.FilterParamName];
      }
      if (this.route.snapshot.queryParams[this.LiveOnlyParamName]) {
        this.isProductionOnly = this.route.snapshot.queryParams[this.LiveOnlyParamName] === "true";
      }
      if (this.route.snapshot.queryParams[this.PageSizeParamName]) {
        this.viewControls.pageSize = this.route.snapshot.queryParams[this.PageSizeParamName];
      }
      if (this.route.snapshot.queryParams[this.StyleParamName]) {
        this.style = this.route.snapshot.queryParams[this.StyleParamName];
      }
      if (this.route.snapshot.queryParams[this.ConfigParamName]) {
        this.config = this.route.snapshot.queryParams[this.ConfigParamName];
      }
      if (this.route.snapshot.queryParams[this.HomeParamName]) {
        this.home = this.route.snapshot.queryParams[this.HomeParamName];
      }
  }

  protected onSelectedCompanyChange(): void {
    this.loadRenderedOutputs();
  }

  getProductColour(productName: ProductName) {
    return this.productNameService.getProductNameConfiguration(productName)?.productColor;
  }

  clearFilter(filter: string) {
    switch(filter) {
      case this.FilterParamName:
        this.filter = "";
        break;
      case this.StyleParamName:
        this.style = undefined;
        break;
      case this.ConfigParamName:
        this.config = undefined;
        break;
      case this.HomeParamName:
        this.home = undefined;
        break;
    }
    this.updateParams();
    this.loadRenderedOutputs();
  }

  toggleIsProductionOnly() {
    this.isProductionOnly = !this.isProductionOnly;
    this.updateParams();
    this.loadRenderedOutputs();
  }

  cancelRenderedOutput(renderedOutput: RenderedOutputSummary) {
    if (!renderedOutput) {
      return;
    }
    this.loading = true;

    const options: ConfirmDialogData = {
      title: 'Cancel rendered output confirmation',
      message: 'Are you sure? Cancelling a rendered output can have very negative consequences.',
      cancelText: 'No',
      confirmText: 'Yes',
      actionIcon: 'delete',
      actionColour: 'warn'
    };
    this.dialogService.open(options);
    this._subscriptions.push(this.dialogService.confirmed()
      .subscribe(confirmed => {
        if (confirmed) {
          let request = new SetRenderedOutputStatusRequest();
          request.renderedOutputId = renderedOutput.artifactId;
          request.status = RenderedOutputStatus.Cancelled;
  
          this.situViewClient.setRenderedOutputStatus(request)
            .then((response: SetRenderedOutputStatusResponse) => {
              if (response.success) {
                this.dataSource.data = this.dataSource.data.filter(x => x.artifactId !== renderedOutput.artifactId);
              } else {
                this.showMessage("Sorry! There was an issue at our end cancelling this rendered output. Please try again.");
              }
            })
            .catch((x: any) => {
              this.showMessage("Sorry! There was an issue at our end cancelling this rendered output. Please try again.");
            })
            .finally(() => {
              this.loading = false;
            });
        } else {
          this.loading = false;
        }
      }));
  }

  onPageChange(event: PageEvent) {
    this.viewControls.pageSize = event.pageSize;
    this.viewControls.pageIndex = event.pageIndex;

    this.loadRenderedOutputs();
  }

  view(renderedOutput: RenderedOutputSummary, newWindow: boolean = false) {
    let url: string;
  
    switch (renderedOutput.productName) {
      case ProductName.Facade:
        url = this.router.serializeUrl(this.router.createUrlTree(['/situ-facade'], { queryParams: { id: renderedOutput.id } }));
        break;
      default:
        url = this.router.serializeUrl(this.router.createUrlTree(['/situ-view'], { queryParams: { id: renderedOutput.id } }));
        break;
    }
  
    if (newWindow) {
      window.open(url, '_blank');
    } else {
      this.router.navigateByUrl(url);
    }
  }

  getFacadeImage(artifactId: string) {
    return `${this.configService.config.situViewWebApiBaseUrl}/home/GetImage?artifactId=${artifactId}&fileName=facade.jpg&width=512`;
  }
  
  getPanoImage(artifactId: string) {
    return `${this.configService.config.situViewWebApiBaseUrl}/home/GetThumb?artifactId=${artifactId}`;
  }

  loadRenderedOutputs() {
    this.loading = true;
    let request = new ListRenderedOutputsRequest();
    request.companyId = this.selectedCompany?.id;
    request.pageNumber = this.viewControls.pageIndex + 1;
    request.pageSize = this.viewControls.pageSize;
    if (this.product !== undefined) request.productName = this.product;
    request.status = RenderedOutputStatus.Complete;
    if (this.isProductionOnly) request.isProduction = true;
    if (this.style) request.styleConfigTemplateId = this.style;
    if (this.config) request.homeConfigId = this.config;
    if (this.home) request.homeId = this.home;
    if (this.filter) request.containsWords = [this.filter];
    if (request.productName === ProductName.Panorama) request.expandProducts = true;

    this.situViewClient.listRenderedOutputs(request)
      .then((response: ListRenderedOutputsResponse) => {
        if (response.success && response?.renderedOutputs) {
          this.dataSource.data = response.renderedOutputs;
          this.viewControls.totalItems = response.totalItems ?? 0;
          this.loading = false;
          this.updateParams();
        } else {
          this.showMessage("Sorry! There was an issue at our end retrieving your renders. Please try again.");
        }
      })
      .catch((x: any) => {
        this.showMessage("Sorry! There was an issue at our end retrieving your renders. Please try again.");
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private updateParams() {
    const queryParams: { [key: string]: any } = {};

    if (this.filter) {
      queryParams.filter = this.filter;
    }
    if (this.isProductionOnly) {
      queryParams.liveOnly = "true";
    }
    if (this.viewControls.pageSize) {
      queryParams.pageSize = this.viewControls.pageSize;
    }
    if (this.style) {
      queryParams.style = this.style;
    }
    if (this.config) {
      queryParams.config = this.config;
    }
    
    const urlTree = this.router.createUrlTree([], {
      queryParamsHandling: "merge",
      preserveFragment: true,
      queryParams: queryParams
    });

    if (!this.filter) {
      delete urlTree.queryParams[this.FilterParamName];
    }
    if (!this.isProductionOnly) {
      delete urlTree.queryParams[this.LiveOnlyParamName];
    }
    if (this.viewControls.pageSize === this.DefaultPageSize) {
      delete urlTree.queryParams[this.PageSizeParamName];
    }
    if (!this.style) {
      delete urlTree.queryParams[this.StyleParamName];
    }
    if (!this.config) {
      delete urlTree.queryParams[this.ConfigParamName];
    }
  
    this.router.navigateByUrl(urlTree);
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}